import React from 'react'
import type { PostCategory } from '../../api/postCategories'

type Props = {
  value: string
  onChange: (slug: string) => void
  options: PostCategory[]
  placeholder?: string
  required?: boolean
  disabled?: boolean
}

export default function CategorySelect({
  value,
  onChange,
  options,
  placeholder = 'Select category',
  required,
  disabled,
}: Props) {
  const [open, setOpen] = React.useState(false)
  const [q, setQ] = React.useState('')
  const rootRef = React.useRef<HTMLDivElement | null>(null)

  const current = options.find((c) => c.slug === value) || null
  const many = options.length > 12
  const filtered = many
    ? options.filter((c) => (c.name + ' ' + c.slug).toLowerCase().includes(q.trim().toLowerCase()))
    : options

  React.useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      const el = rootRef.current
      if (!el) return
      if (e.target instanceof Node && !el.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-2 rounded-xl bg-white/5 border px-3 py-2 text-sm outline-none ${
          disabled ? 'opacity-60' : 'hover:bg-white/10'
        } ${required && !value ? 'border-red-500/60' : 'border-white/10'}`}
      >
        <span className={current ? 'text-white/90' : 'text-white/60'}>{current ? current.name : placeholder}</span>
        <span className="text-white/60" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
            <path
              d="M5.5 7.5L10 12l4.5-4.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {open ? (
        <div className="absolute z-50 mt-2 w-full rounded-xl border border-white/10 bg-neutral-900 shadow-2xl overflow-hidden">
          {many ? (
            <div className="p-2 border-b border-white/10">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search..."
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          ) : null}
          <div role="listbox" aria-label="Categories" className="max-h-64 overflow-auto menu-scroll">
            <button
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 text-white/75"
              onClick={() => {
                onChange('')
                setOpen(false)
              }}
            >
              {placeholder}
            </button>
            {filtered.map((c) => (
              <button
                key={c.slug}
                type="button"
                className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 ${
                  c.slug === value ? 'text-brand font-semibold' : 'text-white/85'
                }`}
                onClick={() => {
                  onChange(c.slug)
                  setOpen(false)
                }}
              >
                {c.name}
              </button>
            ))}
            {!filtered.length ? (
              <div className="px-3 py-2 text-sm text-white/60">No matches</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}
